import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { CartLineItem } from "@/components/cart/CartLineItem";
import { CartSummary } from "@/components/cart/CartSummary";
import { EmptyCart } from "@/components/cart/EmptyCart";
import { ErrorBanner } from "@/components/cart/ErrorBanner";
import { StickyCartBar } from "@/components/cart/StickyCartBar";
import { describeCartError, useCart, useRemoveCartItem, useSetCartItemQuantity } from "@/lib/cart";

/**
 * Page panier complète (VVS_MODELE_INTERFACE.md §7.2, gabarit G4 : 8 colonnes
 * lignes + 4 colonnes récapitulatif collant ; une seule colonne + barre basse
 * collante sous `lg`, VVS_MODELE_RESPONSIVE.md §7.4).
 *
 * Porté depuis apps/web/app/(shop)/panier/page.tsx (Server Component +
 * Server Actions) :
 *
 *  - Lecture du panier : `getCart(ctx)` (server-side, cookie httpOnly lu à la
 *    main) → `useCart()` (TanStack Query, src/lib/cart.ts) ; même choix de ne
 *    PAS distinguer "panier vide" de "API indisponible" (`EmptyCart` dans les
 *    deux cas), cf. commentaire de `fetchCartOrNull()` dans src/lib/cart.ts.
 *  - Mutations quantité/retrait : `revalidatePath` + `redirect(?cartError=…)`
 *    → mutations restant SUR PLACE (`useSetCartItemQuantity`/
 *    `useRemoveCartItem`), erreur affichée en ligne (`actionError`, état
 *    local) plutôt que par navigation — meilleure UX en SPA, pas de flash de
 *    page. Le paramètre `?cartError=` reste néanmoins lu (`searchParams`) car
 *    `CommandePage` continue d'y rediriger quand le panier contient une ligne
 *    indisponible (cf. son commentaire) : cette page doit donc afficher ce cas
 *    précis venu d'ailleurs, en plus des erreurs de ses propres mutations.
 */
export function PanierPage() {
  const [searchParams] = useSearchParams();
  const redirectError = searchParams.get("cartError");

  const { data: cart, isLoading } = useCart();
  const setQuantity = useSetCartItemQuantity();
  const removeItem = useRemoveCartItem();
  const [actionError, setActionError] = useState<string | null>(null);

  const isMutating = setQuantity.isPending || removeItem.isPending;

  function handleSetQuantity(variantId: string, quantity: number) {
    setActionError(null);
    setQuantity.mutate(
      { variantId, quantity },
      { onError: (err) => setActionError(describeCartError(err)) },
    );
  }

  function handleRemove(variantId: string) {
    setActionError(null);
    removeItem.mutate(variantId, { onError: (err) => setActionError(describeCartError(err)) });
  }

  if (isLoading) {
    return (
      <div className="container-vvs py-10">
        <p className="text-sm text-muted">Chargement du panier…</p>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="container-vvs flex flex-col gap-6 py-10">
        {redirectError ? <ErrorBanner message={redirectError} /> : null}
        {actionError ? <ErrorBanner message={actionError} /> : null}
        <EmptyCart />
      </div>
    );
  }

  const hasUnavailableLine = cart.items.some((item) => !item.stock.inStock);

  return (
    <div className="container-vvs flex flex-col gap-6 py-10 pb-24 lg:pb-10">
      {redirectError ? <ErrorBanner message={redirectError} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}

      <h1 className="font-display text-2xl font-semibold text-ink">
        Mon panier — {cart.itemCount} article{cart.itemCount > 1 ? "s" : ""}
      </h1>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="flex flex-col gap-3 lg:col-span-8">
          {cart.items.map((item) => (
            <CartLineItem
              key={item.id}
              item={item}
              onSetQuantity={handleSetQuantity}
              onRemove={handleRemove}
              isMutating={isMutating}
            />
          ))}

          <Link to="/" className="mt-2 w-fit text-sm font-medium text-navy hover:text-gold">
            ‹ Continuer mes achats
          </Link>
        </div>

        <div className="lg:col-span-4">
          <CartSummary cart={cart} />
        </div>
      </div>

      <StickyCartBar
        totalMinor={cart.total.amountMinor}
        label="Commander"
        action={{ type: "link", href: hasUnavailableLine ? "/panier" : "/commande" }}
      />
    </div>
  );
}
